<script lang="ts">
	import type { Snippet } from 'svelte';

	interface Props {
		/** Toggle semantics, e.g. switching table tabs; also drives the selected style. */
		'aria-pressed'?: boolean;
		/** Disclosure semantics, e.g. opening a legend panel; also drives the selected style. */
		'aria-expanded'?: boolean;
		/** Id of the element `aria-expanded` controls. */
		'aria-controls'?: string;
		/** Drops the shadow, for buttons on a flat surface (e.g. the table tabs). */
		flat?: boolean;
		/** Invokes a popover by id (Popover API); pair with `aria-expanded` from its toggle event. */
		popovertarget?: string;
		/** CSS anchor name, so an anchored popover can position against this button. */
		anchorName?: string;
		/** Greys the button out, e.g. a table tab with no rows on the current map view. */
		subtle?: boolean;
		/** Non-interactive + greyed, e.g. module tabs while the AI assistant drives the map. */
		disabled?: boolean;
		/** Native tooltip; the accessible name comes from the content. */
		title?: string;
		children: Snippet;
		onclick?: () => void;
	}

	const {
		'aria-pressed': pressed,
		'aria-expanded': expanded,
		'aria-controls': controls,
		flat = false,
		popovertarget,
		anchorName,
		subtle = false,
		disabled = false,
		title,
		children,
		onclick
	}: Props = $props();

	const selected = $derived(pressed ?? expanded ?? false);
</script>

<button
	type="button"
	class="moduleControl"
	class:active={selected}
	class:subtle
	class:flat
	{title}
	{onclick}
	{disabled}
	aria-pressed={pressed}
	aria-expanded={expanded}
	aria-controls={controls}
	{popovertarget}
	style:anchor-name={anchorName}>
	{@render children()}
</button>

<style>
	button.moduleControl {
		--module-surface: var(--accent);
		--module-text: var(--accent-contrast);
		display: flex;
		position: relative;
		flex-direction: row;
		justify-content: center;
		align-items: center;
		gap: var(--space-2);
		transition:
			background-color 0.15s ease-out,
			box-shadow 0.15s ease-out;
		cursor: pointer;
		box-shadow: var(--shadow-sm);
		border: 0;
		border-radius: var(--radius);
		background-color: var(--module-surface);
		padding-inline: var(--space-3);
		min-height: var(--height-button);
		color: var(--module-text);
		font-weight: var(--weight-semibold);
		font-size: var(--font-size-sm);
		letter-spacing: var(--tracking-caps);
		text-transform: uppercase;
		user-select: none;

		&:hover:not(:disabled) {
			--module-surface: var(--accent-hover);
		}

		&.active {
			--module-surface: var(--accent-active);
		}

		&.flat {
			box-shadow: none;
		}

		/* Selected toggles get a detached underline (not colour alone); an expanded disclosure is marked by the panel hanging off it. */
		&[aria-pressed='true']::after {
			position: absolute;
			right: var(--space-2);
			bottom: -7px;
			left: var(--space-2);
			border-radius: var(--radius-full);
			background-color: var(--accent-text);
			height: 3px;
			content: '';
		}

		&.subtle:not(.active) {
			--module-surface: var(--surface-alt);
			--module-text: var(--text-muted);
			box-shadow: none;

			&:hover:not(:disabled) {
				--module-surface: var(--hover-surface);
			}
		}

		&:disabled {
			--module-surface: var(--disabled-surface);
			--module-text: var(--disabled-text);
			cursor: not-allowed;
			box-shadow: none;
		}
	}
</style>
